/**
 * Utility functions for performance and common operations
 */

/**
 * Throttle function execution
 * @param {Function} fn - Function to throttle
 * @param {number} limit - Time limit in ms
 * @returns {Function}
 */
export function throttle(fn, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Debounce function execution
 * @param {Function} fn - Function to debounce
 * @param {number} wait - Wait time in ms
 * @returns {Function}
 */
export function debounce(fn, wait) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Request Animation Frame wrapper with cleanup
 */
export class RAFLoop {
  #callback;
  #running = false;
  #rafId = null;
  #lastTime = 0;
  #fps = 60;
  #frameInterval = 1000 / 60;

  constructor(callback, options = {}) {
    this.#callback = callback;
    this.#fps = options.fps || 60;
    this.#frameInterval = 1000 / this.#fps;
  }

  start() {
    if (this.#running) return;
    this.#running = true;
    this.#lastTime = performance.now();
    this.#tick();
  }

  stop() {
    this.#running = false;
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #tick = (currentTime = performance.now()) => {
    if (!this.#running) return;

    const deltaTime = currentTime - this.#lastTime;

    if (deltaTime >= this.#frameInterval) {
      this.#lastTime = currentTime - (deltaTime % this.#frameInterval);
      this.#callback(deltaTime);
    }

    this.#rafId = requestAnimationFrame(this.#tick);
  };

  get isRunning() {
    return this.#running;
  }
}

/**
 * Check if reduced motion is preferred
 * @returns {boolean}
 */
export function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Check if touch device
 * @returns {boolean}
 */
export function isTouchDevice() {
  return window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
}

/**
 * Spatial hash grid for O(1) neighbor lookups
 * Replaces O(n²) particle distance checks
 */
export class SpatialHash {
  constructor(cellSize) {
    this.cellSize = cellSize;
    this.grid = new Map();
  }

  clear() {
    this.grid.clear();
  }

  getKey(x, y, z = 0) {
    const cx = Math.floor(x / this.cellSize);
    const cy = Math.floor(y / this.cellSize);
    const cz = Math.floor(z / this.cellSize);
    return `${cx},${cy},${cz}`;
  }

  insert(point, index) {
    const key = this.getKey(point.x, point.y, point.z);
    if (!this.grid.has(key)) {
      this.grid.set(key, []);
    }
    this.grid.get(key).push({ point, index });
  }

  query(x, y, z, radius) {
    const results = [];
    const cellRadius = Math.ceil(radius / this.cellSize);
    const cx = Math.floor(x / this.cellSize);
    const cy = Math.floor(y / this.cellSize);
    const cz = Math.floor(z / this.cellSize);

    for (let dx = -cellRadius; dx <= cellRadius; dx++) {
      for (let dy = -cellRadius; dy <= cellRadius; dy++) {
        for (let dz = -cellRadius; dz <= cellRadius; dz++) {
          const key = `${cx + dx},${cy + dy},${cz + dz}`;
          const cell = this.grid.get(key);
          if (cell) {
            for (const item of cell) {
              const dist = Math.sqrt(
                (item.point.x - x) ** 2 + 
                (item.point.y - y) ** 2 + 
                (item.point.z - z) ** 2
              );
              if (dist <= radius) {
                results.push({ ...item, distance: dist });
              }
            }
          }
        }
      }
    }
    return results;
  }
}

/**
 * Object pool for particle effects
 * Prevents GC pressure from creating/destroying DOM nodes
 */
export class ObjectPool {
  constructor(factory, reset, size = 50) {
    this.factory = factory;
    this.reset = reset;
    this.pool = [];
    this.active = new Set();
    
    // Pre-populate
    for (let i = 0; i < size; i++) {
      this.pool.push(this.factory());
    }
  }

  acquire() {
    let item = this.pool.pop();
    if (!item) {
      item = this.factory();
    }
    this.active.add(item);
    return item;
  }

  release(item) {
    if (this.active.has(item)) {
      this.active.delete(item);
      this.reset(item);
      this.pool.push(item);
    }
  }

  releaseAll() {
    for (const item of this.active) {
      this.reset(item);
      this.pool.push(item);
    }
    this.active.clear();
  }
}

/**
 * Intersection Observer helper with fallback
 */
export function createObserver(callback, options = {}) {
  const defaultOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
    ...options
  };

  if ('IntersectionObserver' in window) {
    return new IntersectionObserver(callback, defaultOptions);
  }

  // Fallback for older browsers
  return {
    observe: (el) => {
      callback([{ target: el, isIntersecting: true }]);
    },
    unobserve: () => {},
    disconnect: () => {}
  };
}

/**
 * Linear interpolation
 */
export function lerp(start, end, t) {
  return start * (1 - t) + end * t;
}

/**
 * Clamp value between min and max
 */
export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
